import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "./ui/button";
import { Rocket, Sparkles, Mail, Star } from "lucide-react";

export const HeroSection = ({ onNavigate }) => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 1000], [0, 100]);

  return (
    <section
      id="about"
      className="relative py-20 sm:py-24 lg:py-32 overflow-hidden bg-gradient-to-b from-[hsl(var(--background))] via-[hsl(var(--card))]/30 to-[hsl(var(--background))]"
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-0 grid-pattern opacity-20" style={{ y: y1 }} />
      <div className="relative mx-auto px-4 sm:px-6 lg:px-10 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", duration: 0.8 }}
            className="flex justify-center lg:justify-start"
            data-testid="hero-avatar"
          >
            <div className="relative">
              <div className="w-72 h-72 rounded-2xl border-2 border-[hsl(var(--primary))]/30 shadow-2xl overflow-hidden bg-[hsl(var(--card))] animate-float ring-4 ring-[hsl(var(--primary))]/10">
                <img src="/ryan-profile.png" alt="Ryan M Colon" className="w-full h-full object-cover" />
              </div>
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent))] rounded-xl flex items-center justify-center shadow-lg glow-strong"
              >
                <Star className="w-6 h-6 text-white fill-white" />
              </motion.div>
            </div>
          </motion.div>

          <div className="space-y-6 text-center lg:text-left" data-testid="hero-content">
            <motion.div initial={{ x: -100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
              <h1
                className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight bg-gradient-to-r from-[hsl(var(--foreground))] to-[hsl(var(--primary))] bg-clip-text text-transparent"
                data-testid="hero-title"
              >
                Ryan M Colon
              </h1>
            </motion.div>

            <motion.div initial={{ x: -100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
              <h2 className="font-mono text-lg sm:text-xl text-[hsl(var(--primary))] font-semibold" data-testid="hero-subtitle">
                Entrepreneur • Affiliate • Digital Creator
              </h2>
            </motion.div>

            <motion.p
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-lg sm:text-xl leading-relaxed text-[hsl(var(--muted-foreground))] max-w-2xl"
              data-testid="hero-description"
            >
              Live Life Unbound. Mastering the digital landscape through strategic marketing, powerful automation,
              and content that converts. Building wealth and freedom one campaign at a time.
            </motion.p>

            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-4 justify-center lg:justify-start"
              data-testid="hero-cta-container"
            >
              <Button
                onClick={() => onNavigate("projects")}
                className="modern-button px-8 py-6 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 font-semibold text-base shadow-lg hover:shadow-xl glow"
                data-testid="hero-primary-cta"
              >
                <Rocket className="w-5 h-5 mr-2" />
                Browse Shop
              </Button>
              <Button
                onClick={() => onNavigate("network")}
                variant="outline"
                className="modern-button px-8 py-6 rounded-xl border-2 border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] hover:border-[hsl(var(--primary))] font-semibold text-base"
                data-testid="hero-secondary-cta"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Explore Network
              </Button>
              <Button
                onClick={() => onNavigate("contact")}
                variant="outline"
                className="modern-button px-8 py-6 rounded-xl border-2 border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] hover:border-[hsl(var(--primary))] font-semibold text-base"
                data-testid="hero-tertiary-cta"
              >
                <Mail className="w-5 h-5 mr-2" />
                Hire Me
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
